// Email capture — Klaviyo client-side subscribe + post-signup animation.
//
// Progressive enhancement for the `email-capture` section. Each capture block renders a real <form>
// (snippets/email-capture-form.liquid) with an input "face" and a hidden success "face". The Klaviyo
// connection (public company id + list id) and the chosen animation live on the section wrapper
// (`[data-email-capture]`) as data-attributes — so one section can hold several blocks that all share
// one connection. We intercept submit, POST to Klaviyo's CLIENT subscriptions endpoint (public, no
// secret key — it only needs the public company id), then flip the form to its success face using the
// section's animation: an animated checkmark, a confetti burst, or a 3D card flip.
//
// No-JS / failure behaviour: there's no native Klaviyo form action, so without JS the form simply
// can't submit — nothing is lost or mis-sent. In the theme editor we never call Klaviyo; we just
// preview the success animation so the merchant can see it.

import { consentApiReady, marketingConsentAllowed } from '@theme/consent';
import { formatUsPhone, normalizePhone } from '@theme/consent';
import { EMAIL_RE, inEditor, postClient, readErrors } from '@theme/klaviyo-client';

const SECTION = '[data-email-capture]';

const reducedMotion = () =>
  window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Subscribe a profile to a Klaviyo list via the public client endpoint. Resolves on 2xx, throws
// otherwise. `email` is required; `firstName` optional.
async function klaviyoSubscribe(company, listId, email, firstName) {
  const profileAttributes = { email };
  if (firstName) profileAttributes.first_name = firstName;

  const body = {
    data: {
      type: 'subscription',
      attributes: {
        custom_source: 'Storefront email capture',
        profile: { data: { type: 'profile', attributes: profileAttributes } },
      },
      relationships: { list: { data: { type: 'list', id: listId } } },
    },
  };

  const res = await postClient('subscriptions', company, body);

  // The client endpoint returns 202 Accepted with an empty body on success.
  if (!res.ok) throw new Error((await readErrors(res)) || `Subscribe failed (${res.status})`);
}

// ---- Optional SMS step (slides in after a successful email subscribe) ----

function maskPhone(input) {
  const value = input.value;
  if (value.trim().startsWith('+')) return;
  const formatted = formatUsPhone(value);
  if (formatted !== value) input.value = formatted;
}

// Subscribe a phone number (SMS) to the same Klaviyo list. Consent is governed by the list's opt-in
// settings; the list must be SMS-enabled for the consent to stick. Resolves on 2xx.
//
// Pass `email` whenever we have it: Klaviyo resolves profile identity by email/phone/external_id, so
// a phone-only payload can't be matched to the profile the email step just created and forks a
// separate phone-only profile instead of adding SMS consent to the shopper's. Same reason
// ca-back-in-stock.js sends both channels' identifiers in ONE call. Note this API revision rejects an
// inline `subscriptions` object, so the identifier IS the consent grant here.
async function klaviyoSubscribeSms(company, listId, phone, email) {
  const profileAttributes = { phone_number: phone };
  if (email) profileAttributes.email = email;

  const body = {
    data: {
      type: 'subscription',
      attributes: {
        custom_source: 'Newsletter SMS',
        profile: { data: { type: 'profile', attributes: profileAttributes } },
      },
      relationships: { list: { data: { type: 'list', id: listId } } },
    },
  };
  const res = await postClient('subscriptions', company, body);
  if (!res.ok) throw new Error((await readErrors(res)) || `SMS subscribe failed (${res.status})`);
}

// Slide the SMS opt-in step in (hides the email face via the .is-sms class on the shell).
function showSmsStep(formShell) {
  formShell.classList.remove('is-loading');
  formShell.classList.add('is-sms');
  const smsFace = formShell.querySelector('[data-ec-sms]');
  if (smsFace) {
    smsFace.hidden = false;
    const phone = smsFace.querySelector('[data-ec-phone]');
    window.setTimeout(() => {
      try {
        phone?.focus({ preventScroll: true });
      } catch (_) {
        phone?.focus();
      }
    }, 60);
  }
}

// Leave the SMS step (submitted or skipped) and run the normal success reveal/animation.
function finishToSuccess(formShell, section) {
  formShell.classList.remove('is-loading');
  const smsFace = formShell.querySelector('[data-ec-sms]');
  if (smsFace) smsFace.hidden = true;
  formShell.classList.remove('is-sms');
  const animation = section ? section.dataset.successAnimation || 'checkmark' : 'checkmark';
  const accentColors = section ? [section.dataset.accent1, section.dataset.accent2].filter(Boolean) : [];
  showSuccess(formShell, animation, accentColors);
}

function showSmsError(smsFace, message) {
  const el = smsFace.querySelector('[data-ec-sms-error]');
  if (el) {
    el.textContent = message;
    el.hidden = false;
  }
}

async function handleSmsSubmit(event) {
  const btn = event.target.closest('[data-ec-sms-submit]');
  const smsFace = btn && btn.closest('[data-ec-sms]');
  const formShell = btn && btn.closest('[data-ec-form]');
  if (!smsFace || !formShell) return;
  if (formShell.classList.contains('is-loading')) return;

  const section = formShell.closest(SECTION);
  const d = (section && section.dataset) || {};
  const errEl = smsFace.querySelector('[data-ec-sms-error]');
  if (errEl) errEl.hidden = true;

  const phoneInput = smsFace.querySelector('[data-ec-phone]');
  const consent = smsFace.querySelector('[data-ec-sms-consent]');
  const phone = normalizePhone(phoneInput && phoneInput.value);
  if (!phone) {
    showSmsError(smsFace, d.errPhone || 'Please enter a valid mobile number.');
    phoneInput?.focus();
    return;
  }
  if (!(consent && consent.checked)) {
    showSmsError(smsFace, d.errSmsConsent || 'Please check the box to agree to receive texts.');
    consent?.focus();
    return;
  }

  // Theme editor / not-configured: skip Klaviyo, just complete the flow so the merchant sees it.
  const company = (d.klCompany || '').trim();
  const listId = (d.klList || '').trim();
  if (inEditor() || !company || !listId) {
    finishToSuccess(formShell, section);
    return;
  }

  formShell.classList.add('is-loading');
  try {
    // Read the email straight off the still-present input rather than stashing it on the shell's
    // dataset — a data- attribute would put the shopper's email into serialized HTML, which
    // session-replay tools don't mask (input values usually are). Degrades to a phone-only call.
    // Re-validate rather than trust the field: attaching this phone to a DIFFERENT shopper's
    // profile is worse than attaching it to none, so anything invalid degrades to phone-only.
    const emailInput = formShell.querySelector('[data-ec-input] input[name="email"]');
    const candidate = (emailInput?.value || '').trim();
    await klaviyoSubscribeSms(company, listId, phone, EMAIL_RE.test(candidate) ? candidate : '');
  } catch (error) {
    // Best-effort — the email signup already succeeded; don't trap the shopper on the SMS step.
    if (window.console) console.warn('[email-capture] sms subscribe', error);
  }
  finishToSuccess(formShell, section);
}

// Reveal the success face. Adds `.is-success` (CSS drives the checkmark/flip), moves the input face
// out of the tab order, and shifts focus to the success message so screen readers announce it.
function showSuccess(formShell, animation, accentColors) {
  formShell.classList.remove('is-loading');
  formShell.classList.add('is-success');

  const inputFace = formShell.querySelector('[data-ec-input]');
  const successFace = formShell.querySelector('[data-ec-success]');

  if (inputFace) {
    inputFace.setAttribute('aria-hidden', 'true');
    inputFace.querySelectorAll('input, button').forEach((el) => {
      el.disabled = true;
    });
  }

  if (successFace) {
    successFace.setAttribute('aria-hidden', 'false');
    successFace.setAttribute('tabindex', '-1');
    // Let the flip/fade begin before pulling focus (focus would otherwise scroll it into view abruptly).
    window.setTimeout(() => {
      try {
        successFace.focus({ preventScroll: true });
      } catch (_) {
        successFace.focus();
      }
    }, 60);
  }

  if (animation === 'confetti' && !reducedMotion()) {
    const canvas = formShell.querySelector('[data-ec-confetti]');
    if (canvas) burstConfetti(canvas, accentColors);
  }
}

function showError(formShell, message) {
  formShell.classList.remove('is-loading');
  const errorEl = formShell.querySelector('[data-ec-error]');
  if (errorEl) {
    errorEl.textContent = message;
    errorEl.hidden = false;
  }
}

function clearError(formShell) {
  const errorEl = formShell.querySelector('[data-ec-error]');
  if (errorEl) {
    errorEl.hidden = true;
    errorEl.textContent = '';
  }
  formShell.querySelectorAll('.is-attn').forEach((el) => el.classList.remove('is-attn'));
}

async function handleSubmit(event) {
  const form = event.target.closest('[data-ec-input]');
  if (!form) return;
  event.preventDefault();

  const section = form.closest(SECTION);
  const formShell = form.closest('[data-ec-form]');
  if (!section || !formShell) return;
  if (formShell.classList.contains('is-loading') || formShell.classList.contains('is-success')) return;

  const emailInput = form.querySelector('input[name="email"]');
  const nameInput = form.querySelector('input[name="first_name"]');
  const email = (emailInput && emailInput.value || '').trim();
  const firstName = (nameInput && nameInput.value || '').trim();

  clearError(formShell);

  // Merchant-editable copy (Theme editor → Form messages), with the original strings as fallbacks.
  const d = section.dataset;
  const MSG = {
    email: d.errEmail || 'Please enter a valid email address.',
    unconfigured: d.errUnconfigured || 'Signups aren’t connected yet. Please try again later.',
    consent: d.errConsent || 'We couldn’t confirm your marketing preferences — check the box below to continue.',
    generic: d.errGeneric || 'Something went wrong. Please try again.',
  };

  if (!EMAIL_RE.test(email)) {
    showError(formShell, MSG.email);
    if (emailInput) emailInput.focus();
    return;
  }

  const animation = section.dataset.successAnimation || 'checkmark';
  const company = (section.dataset.klCompany || '').trim();
  const listId = (section.dataset.klList || '').trim();

  // Accent colors (Aurora style) tint the confetti so the celebration matches the section palette.
  const accentColors = [section.dataset.accent1, section.dataset.accent2].filter(Boolean);

  const smsEnabled = section.dataset.sms === 'true';

  // Theme editor: never hit Klaviyo — just preview the flow (SMS step if enabled, else the animation).
  if (inEditor()) {
    if (smsEnabled) showSmsStep(formShell);
    else showSuccess(formShell, animation, accentColors);
    return;
  }

  if (!company || !listId) {
    showError(formShell, MSG.unconfigured);
    return;
  }

  // Don't transmit PII to Klaviyo without marketing consent (CMP/region/GPC aware). Rather than
  // dead-end a GPC/opt-out visitor, reveal a just-in-time consent checkbox — an explicit,
  // purpose-specific opt-in overrides the automated signal for this first-party subscription.
  await consentApiReady;
  const gpcConsent = form.querySelector('[data-ec-gpc-consent]');
  if (!marketingConsentAllowed() && !(gpcConsent && gpcConsent.checked)) {
    const gpcWrap = form.querySelector('[data-ec-gpc]');
    if (gpcWrap) {
      gpcWrap.hidden = false;
      gpcWrap.classList.add('is-attn'); // draw the eye to the box that must be checked to continue
      try {
        gpcWrap.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      } catch (_) {
        /* focus below still reveals it */
      }
    }
    // Friendly, built-in prompt (not the merchant error_consent setting) so it always points to the
    // revealed checkbox in plain language, regardless of any template override.
    showError(formShell, 'Almost there! Just check the box below to finish signing up.');
    if (gpcConsent) gpcConsent.focus();
    return;
  }

  formShell.classList.add('is-loading');

  try {
    await klaviyoSubscribe(company, listId, email, firstName);
    // Email-first: on success, slide in the optional SMS step when enabled; otherwise finish now.
    if (smsEnabled) showSmsStep(formShell);
    else showSuccess(formShell, animation, accentColors);
  } catch (error) {
    showError(formShell, MSG.generic);
    if (window.console) console.warn('[email-capture]', error);
  }
}

// ---- Confetti ----
// A small, dependency-free burst that paints onto the form's overlay canvas and tears itself down
// when the particles settle. Particles fire upward from the canvas centre, fall under gravity, and
// fade. Sized to the form so it never spills outside the block.
function burstConfetti(canvas, accentColors) {
  const shell = canvas.closest('[data-ec-form]') || canvas.parentElement;
  const rect = (shell || canvas).getBoundingClientRect();
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const width = Math.max(rect.width, 1);
  const height = Math.max(rect.height, 1);

  canvas.width = width * dpr;
  canvas.height = height * dpr;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.scale(dpr, dpr);

  // Default to the brand palette; if the section supplies accent colors (Aurora style), fold them in.
  const colors =
    accentColors && accentColors.length
      ? accentColors.concat(['#F2D9C2', '#FFFFFF'])
      : ['#E8907D', '#C25B43', '#F2D9C2', '#7A6A58', '#FFFFFF'];
  const count = 90;
  const originX = width / 2;
  const originY = height / 2;
  const particles = [];

  for (let i = 0; i < count; i += 1) {
    const angle = Math.PI * (0.25 + Math.random() * 0.5); // mostly upward fan
    const speed = 4 + Math.random() * 6;
    const dir = Math.random() < 0.5 ? -1 : 1;
    particles.push({
      x: originX,
      y: originY,
      vx: Math.cos(angle) * speed * dir,
      vy: -Math.sin(angle) * speed - 2,
      size: 5 + Math.random() * 5,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.3,
      color: colors[(Math.random() * colors.length) | 0],
      life: 1,
    });
  }

  const gravity = 0.18;
  let frame = 0;
  const maxFrames = 140;

  function tick() {
    ctx.clearRect(0, 0, width, height);
    let alive = false;

    for (const p of particles) {
      p.vy += gravity;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vr;
      if (frame > 60) p.life -= 0.02;

      if (p.life > 0 && p.y < height + 20) {
        alive = true;
        ctx.save();
        ctx.globalAlpha = Math.max(p.life, 0);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx.restore();
      }
    }

    frame += 1;
    if (alive && frame < maxFrames) {
      requestAnimationFrame(tick);
    } else {
      ctx.clearRect(0, 0, width, height);
    }
  }

  requestAnimationFrame(tick);
}

// One delegated submit listener covers every capture form on the page, including blocks added later
// in the theme editor.
document.addEventListener('submit', (event) => {
  if (event.target && event.target.matches && event.target.matches('[data-ec-input]')) {
    handleSubmit(event);
  }
});

// Ticking the flagged privacy-consent box clears its error + red outline immediately (no resubmit).
document.addEventListener('change', (event) => {
  const target = event.target;
  if (target && target.matches && target.matches('[data-ec-gpc-consent]') && target.checked) {
    const formShell = target.closest('[data-ec-form]');
    if (formShell) clearError(formShell);
  }
});

// Optional SMS step: "Text me" submit, "No thanks" skip, and live phone masking. Delegated so it
// covers every capture form on the page (and any added later in the theme editor).
document.addEventListener('click', (event) => {
  const t = event.target;
  if (!t || !t.closest) return;
  if (t.closest('[data-ec-sms-submit]')) {
    handleSmsSubmit(event);
  } else if (t.closest('[data-ec-sms-skip]')) {
    const formShell = t.closest('[data-ec-form]');
    if (formShell) finishToSuccess(formShell, formShell.closest(SECTION));
  }
});

document.addEventListener('input', (event) => {
  if (event.target && event.target.matches && event.target.matches('[data-ec-phone]')) {
    maskPhone(event.target);
  }
});
